// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * API client for the Brokerage Integration Providers endpoint group.
 *
 * Backed by /api/admin/brokerages/{id}/integration-providers/** on
 * admin-bff (which proxies to resource-server). Platform-admin-only:
 * the server returns 403 for any non-platform-admin role.
 */

export type Provider = 'MICROSOFT' | 'GOOGLE';

export type ValidationResult =
  | 'NEVER_VALIDATED'
  | 'VALID'
  | 'INVALID_CREDENTIALS'
  | 'SCOPE_MISMATCH'
  | 'UNREACHABLE';

export type AdvisorJitProvisioningMode = 'AUTO_ACTIVATE' | 'REQUIRE_ADMIN_APPROVAL';

export interface BrokerageIntegrationProvider {
  id: string;
  brokerageId: string;
  provider: Provider;
  displayName?: string;
  clientId: string;
  tenantId?: string;
  clientSecretConfigured: boolean;

  ssoEnabled: boolean;
  calendarReadEnabled: boolean;
  calendarWriteEnabled: boolean;
  emailReadEnabled: boolean;
  emailSendEnabled: boolean;

  crossAdvisorVisibilityEnabled: boolean;
  advisorJitProvisioningMode: AdvisorJitProvisioningMode;
  attributeMapping?: Record<string, string>;

  lastValidatedAt?: string;
  lastValidationResult: ValidationResult;
  lastValidationMessage?: string;
  grantedScopes: string[];

  contactEmail?: string;
  contactName?: string;

  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface ConfigureProviderRequest {
  clientId: string;
  clientSecret: string;
  tenantId?: string;
  displayName?: string;
  attributeMapping?: Record<string, string>;
  advisorJitProvisioningMode?: AdvisorJitProvisioningMode;
  crossAdvisorVisibilityEnabled?: boolean;
  contactEmail?: string;
  contactName?: string;
}

export interface UpdateCapabilitiesRequest {
  ssoEnabled?: boolean;
  calendarReadEnabled?: boolean;
  calendarWriteEnabled?: boolean;
  emailReadEnabled?: boolean;
  emailSendEnabled?: boolean;
}

export interface ValidateProviderResponse {
  result: ValidationResult;
  valid: boolean;
  message: string;
  grantedScopes: string[];
}

function base(brokerageId: string): string {
  return `/api/admin/brokerages/${brokerageId}/integration-providers`;
}

async function handle<T>(response: Response): Promise<T> {
  if (response.ok) {
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  }
  let message = response.statusText;
  try {
    const body = await response.json();
    message = body.message || body.error || message;
  } catch {
    /* ignore — body wasn't JSON */
  }
  throw new Error(`${response.status}: ${message}`);
}

export const brokerageIntegrationProviderApi = {
  async list(brokerageId: string): Promise<BrokerageIntegrationProvider[]> {
    const response = await fetch(base(brokerageId), { credentials: 'include' });
    return handle<BrokerageIntegrationProvider[]>(response);
  },

  async get(brokerageId: string, provider: Provider): Promise<BrokerageIntegrationProvider> {
    const response = await fetch(`${base(brokerageId)}/${provider}`, { credentials: 'include' });
    return handle<BrokerageIntegrationProvider>(response);
  },

  async configure(
    brokerageId: string,
    provider: Provider,
    request: ConfigureProviderRequest,
  ): Promise<BrokerageIntegrationProvider> {
    const response = await fetch(`${base(brokerageId)}/${provider}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    return handle<BrokerageIntegrationProvider>(response);
  },

  async validate(
    brokerageId: string,
    provider: Provider,
    request: ConfigureProviderRequest,
  ): Promise<ValidateProviderResponse> {
    const response = await fetch(`${base(brokerageId)}/${provider}/validate`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    return handle<ValidateProviderResponse>(response);
  },

  async revalidate(
    brokerageId: string,
    provider: Provider,
  ): Promise<ValidateProviderResponse> {
    const response = await fetch(`${base(brokerageId)}/${provider}/revalidate`, {
      method: 'POST',
      credentials: 'include',
    });
    return handle<ValidateProviderResponse>(response);
  },

  async updateCapabilities(
    brokerageId: string,
    provider: Provider,
    request: UpdateCapabilitiesRequest,
  ): Promise<BrokerageIntegrationProvider> {
    const response = await fetch(`${base(brokerageId)}/${provider}/capabilities`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    return handle<BrokerageIntegrationProvider>(response);
  },

  async delete(brokerageId: string, provider: Provider): Promise<void> {
    const response = await fetch(`${base(brokerageId)}/${provider}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    return handle<void>(response);
  },
};
